import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MdCloudUpload, MdClose } from "react-icons/md";
import ExcelJS from "exceljs";
import { toast } from "react-toastify";

import { dishImportActions } from "../../../../store/slices/dishes-slices";
import { menuCategoriesActions } from "../../../../store/slices/settings-slices";
import css from "./ImportDishesXLSModal.module.css";

const VARIANTS = [
    { suffix: "S", prefix: "Shred" },
    { suffix: "M", prefix: "Maintenance" },
    { suffix: "L", prefix: "Muscle" },
];

// ExcelJS cell values come in several shapes: plain scalar, { richText },
// { text, hyperlink }, { result } for formulas, or Date. Flatten them all.
const cellText = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") {
        if (Array.isArray(v.richText)) return v.richText.map((r) => r.text).join("");
        if (v.text !== undefined) return String(v.text);
        if (v.result !== undefined) return String(v.result);
        if (v.error !== undefined) return "";
        if (v instanceof Date) return v.toISOString();
    }
    return String(v);
};

const cellNum = (v) => {
    const n = Number(cellText(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
};

// Plain JSON, not FormData — the bulk endpoint takes a real JSON body, so
// booleans stay booleans and numbers stay numbers.
const buildDish = ({ title, suffix, ingredients, calories, protein, carbs, fats, categoryIds }) => {
    const nameStr = `${title} ${suffix}`;
    return {
        name: { en: nameStr, ru: nameStr, ge: nameStr, ar: nameStr },
        description: { en: "", ru: "", ge: "", ar: "" },
        recipe: { en: ingredients || "", ru: "", ge: "", ar: "" },
        img: "placeholder",
        cost: 0,
        price: 0,
        weight: 0,
        weightNetto: 0,
        categories: categoryIds,
        type: "REGULAR",
        energy: calories,
        proteins: protein,
        fats: fats,
        carbohydrates: carbs,
        energyOverridden: true,
        proteinsOverridden: true,
        fatsOverridden: true,
        carbohydratesOverridden: true,
        costOverridden: false,
    };
};

/**
 * Upload + category-mapping dialog. Handing the actual import to the
 * dishImport thunk keeps it running — and its progress chip visible — after
 * this modal closes and after the admin leaves the /dishes route.
 */
const ImportDishesXLSModal = ({ setIsOpen }) => {
    const dispatch = useDispatch();
    // Nothing else on the /dishes screen loads categories, so the store is
    // empty unless the admin happened to visit Settings first — fetch our own.
    const { menuCategories, status: categoriesStatus } = useSelector((store) => store.menuCategories);
    const { phase } = useSelector((store) => store.dishImport);
    const categories = menuCategories || [];
    const [selectedFile, setSelectedFile] = useState(null);
    const [step, setStep] = useState("upload"); // "upload" | "map"
    const [parsedRows, setParsedRows] = useState([]);
    const [typeMap, setTypeMap] = useState({});
    const fileInputRef = useRef();

    useEffect(() => {
        dispatch(menuCategoriesActions.getAllMenuCategories());
    }, [dispatch]);

    const getCatName = (cat) => {
        const n = cat.name;
        if (!n) return cat.id;
        if (typeof n === "string") return n;
        return n.en || n.ru || n.ge || n.ar || cat.id;
    };

    const handleFileChange = (e) => {
        if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
    };
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.[0]) setSelectedFile(e.dataTransfer.files[0]);
    };

    const handleNext = async () => {
        if (!selectedFile) return;
        try {
            const buffer = await selectedFile.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);
            const sheet = workbook.worksheets[0];
            if (!sheet) { toast.error("Workbook has no sheets."); return; }

            const headers = {};
            sheet.getRow(1).eachCell((cell, colNum) => {
                headers[colNum] = cellText(cell.value).trim();
            });

            const rows = [];
            sheet.eachRow((row, rowNum) => {
                if (rowNum === 1) return;
                const rowData = {};
                row.eachCell((cell, colNum) => {
                    const key = headers[colNum];
                    if (key) rowData[key] = cell.value;
                });
                if (cellText(rowData["Title"]).trim()) rows.push(rowData);
            });

            if (rows.length === 0) { toast.error("No data rows found in file."); return; }

            setParsedRows(rows);
            const uniqueTypes = [
                ...new Set(rows.map((r) => cellText(r["Type"]).trim()).filter(Boolean)),
            ];
            const initial = {};
            uniqueTypes.forEach((t) => { initial[t] = ""; });
            setTypeMap(initial);
            setStep("map");
        } catch (err) {
            toast.error("Failed to parse file: " + err.message);
        }
    };

    const handleImport = () => {
        // Flatten row x variant into the full payload before dispatching, so a
        // duplicate-name rejection happens before any writes land.
        const payload = [];
        for (const row of parsedRows) {
            const title = cellText(row["Title"]).trim();
            const type = cellText(row["Type"]).trim();
            const catId = typeMap[type];
            const categoryIds = catId ? [catId] : [];

            for (const { suffix, prefix } of VARIANTS) {
                payload.push(
                    buildDish({
                        title,
                        suffix,
                        categoryIds,
                        ingredients: cellText(row[`${prefix} Ingredients`]),
                        calories: cellNum(row[`${prefix} Calories`]),
                        protein: cellNum(row[`${prefix} Protein`]),
                        carbs: cellNum(row[`${prefix} Carbs`]),
                        fats: cellNum(row[`${prefix} Fats`]),
                    })
                );
            }
        }

        // The server rejects duplicate names, but only within a single batch —
        // two identical titles far apart in the sheet would land in different
        // batches and silently become an update. Check the whole payload here.
        const seen = new Set();
        const dupes = new Set();
        payload.forEach((d) => {
            const key = d.name.en.trim().toLowerCase();
            if (seen.has(key)) dupes.add(d.name.en);
            seen.add(key);
        });
        if (dupes.size > 0) {
            toast.error(
                `Duplicate dish titles in file: ${[...dupes].slice(0, 3).join(", ")}` +
                (dupes.size > 3 ? ` and ${dupes.size - 3} more` : "")
            );
            return;
        }

        // Fire and forget — the thunk owns the loop from here, so closing this
        // modal (or navigating away) does not interrupt or hide the import.
        dispatch(dishImportActions.runDishImport({ dishes: payload }));
        setIsOpen(false);
    };

    return (
        <div className={css.overlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setIsOpen(false)}>
                    <MdClose />
                </button>

                <h2 className={css.title}>Import Dishes from XLS</h2>

                {step === "upload" && (
                    <>
                        <p className={css.hint}>
                            Each row creates <strong>3 dishes (S / M / L)</strong>. Required columns: Title, Type, Shred / Maintenance / Muscle — Calories, Protein, Carbs, Fats.
                        </p>
                        <div
                            className={css.dropzone}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <MdCloudUpload className={css.uploadIcon} />
                            {selectedFile ? (
                                <span className={css.fileName}>{selectedFile.name}</span>
                            ) : (
                                <span>Drop .xlsx file here or <strong>click to upload</strong></span>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileChange}
                                className={css.fileInput}
                            />
                        </div>
                        <button className={css.importButton} onClick={handleNext} disabled={!selectedFile}>
                            Next: Map Categories →
                        </button>
                    </>
                )}

                {step === "map" && (
                    <>
                        <p className={css.hint}>
                            Map each <strong>Type</strong> from your file to a category. Leave blank to import without a category.
                        </p>
                        {categoriesStatus === "Loading" && categories.length === 0 && (
                            <p className={css.hint}>Loading categories…</p>
                        )}
                        {categoriesStatus === "fulfilled" && categories.length === 0 && (
                            <p className={css.warn}>
                                No categories exist for this brand yet — create them in Settings first, or import without a category.
                            </p>
                        )}
                        {phase === "importing" && (
                            <p className={css.warn}>
                                An import is already running — wait for it to finish before starting another.
                            </p>
                        )}
                        <div className={css.mapTable}>
                            {Object.keys(typeMap).map((xlsType) => (
                                <div key={xlsType} className={css.mapRow}>
                                    <span className={css.mapLabel}>{xlsType}</span>
                                    <select
                                        className={css.mapSelect}
                                        value={typeMap[xlsType]}
                                        onChange={(e) => setTypeMap((prev) => ({ ...prev, [xlsType]: e.target.value }))}
                                    >
                                        <option value="">— no category —</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>{getCatName(cat)}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                        <div className={css.mapFooter}>
                            <button className={css.backButton} onClick={() => setStep("upload")}>← Back</button>
                            <button
                                className={css.importButton}
                                onClick={handleImport}
                                disabled={phase === "importing"}
                            >
                                Import {parsedRows.length} rows ({parsedRows.length * VARIANTS.length} dishes)
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export { ImportDishesXLSModal };
